import {
  useState,
  useRef,
  useEffect,
  useImperativeHandle,
  useCallback,
} from 'react'
import { useDrag } from './useDrag'

const INITIAL_POSITION = { x: 0, y: 0 }
const DEGREES_PER_ROTATION = 20
const INCREASE_DECREASE_PERCENTAGE = 0.2

export const useImageProcessing = ({
  overlayImg,
  activeImg,
  onChangePhoto,
  parentRef,
}) => {
  const canvasRef = useRef(null)

  const [position, setPosition] = useState(INITIAL_POSITION)
  const [imageCanvas, setImageCanvas] = useState(null)
  const [activeImageObject, setActiveImageObject] = useState(null)
  const [overlayImageObject, setOverlayImageObject] = useState(null)

  useImperativeHandle(parentRef, () => ({
    exportDataURL() {
      let ret = ''
      try {
        ret = imageCanvas.toDataURL({
          format: 'jpeg',
          quality: 1,
          multiplier: 2,
          enableRetinaScaling: true,
        })
      } catch (e) {
        console.log(`problem exporting canvas: ${e}`)
      }
      return ret
    },
  }))

  const { handleMouseDown, handleMouseUp, handleMouseMove } = useDrag(
    (lastPosition, newPosition) => {
      setPosition({
        x: position.x + newPosition.x - lastPosition.x,
        y: position.y + newPosition.y - lastPosition.y,
      })

      setActiveImageObject(prev => ({
        ...prev,
        x: prev.x + newPosition.x - lastPosition.x,
        y: prev.y + newPosition.y - lastPosition.y,
      }))
    },
  )

  const zoomPhotoIn = useCallback(() => {
    setActiveImageObject(prev => ({
      ...prev,
      width: prev.width * (1 + INCREASE_DECREASE_PERCENTAGE),
      height: prev.height * (1 + INCREASE_DECREASE_PERCENTAGE),
    }))
  }, [])

  const zoomPhotoOut = useCallback(() => {
    setActiveImageObject(prev => ({
      ...prev,
      width: prev.width * (1 - INCREASE_DECREASE_PERCENTAGE),
      height: prev.height * (1 - INCREASE_DECREASE_PERCENTAGE),
    }))
  }, [])

  const rotateRight = useCallback(() => {
    setActiveImageObject(prev => ({
      ...prev,
      angle: prev.angle + DEGREES_PER_ROTATION,
    }))
  }, [])

  const rotateLeft = useCallback(() => {
    setActiveImageObject(prev => ({
      ...prev,
      angle: prev.angle - DEGREES_PER_ROTATION,
    }))
  }, [])

  const changePhoto = useCallback(() => {
    onChangePhoto()
  }, [onChangePhoto])

  const getFrameImageinDocument = id => {
    try {
      if (window) {
        let imgElem = document.getElementById(id)
        let tempCanvas = document.createElement('canvas')
        tempCanvas.width = imgElem.naturalWidth
        tempCanvas.height = imgElem.naturalHeight
        tempCanvas.getContext('2d').drawImage(imgElem, 0, 0)
        let dataSrc = tempCanvas.toDataURL('image/png')
        tempCanvas.remove()
        return dataSrc
      }
    } catch (e) {
      console.warn(`issue exporting frame data: ${e}`)
    }
  }

  const setCanvasDimensions = (img, inputCanvasElement = canvasRef.current) => {
    if (window && document) {
      inputCanvasElement.width = img.naturalWidth
      inputCanvasElement.height = img.naturalHeight
      console.log(
        `set canvas dimensions: w:${inputCanvasElement.width} h:${inputCanvasElement.height}`,
      )
    }
  }

  const initCanvas = async (firstOverlayImage, firstActiveImage) => {
    if (window && imageCanvas === null) {
      console.log('init canvas')
      setImageCanvas('')

      let canvasElement = canvasRef.current
      let canvas = canvasElement.getContext('2d')
      setImageCanvas(canvasElement)

      if (firstOverlayImage) {
        let img = await getImg(getFrameImageinDocument(firstOverlayImage), {
          crossOrigin: 'anonymous',
        })
        setCanvasDimensions(img, canvasElement)
        await overlayImageChange(firstOverlayImage, canvasElement)
      }
      if (firstActiveImage) {
        await activeImageChange(firstActiveImage, canvasElement)
      }
    }
  }

  const overlayImageChange = async (
    imageID,
    inputCanvasElement = imageCanvas,
  ) => {
    let dataurl = await getFrameImageinDocument(imageID)
    let img = await getImg(dataurl, { crossOrigin: 'anonymous' })

    const inputCanvas = inputCanvasElement.getContext('2d')
    inputCanvas.clearRect(
      0,
      0,
      inputCanvasElement.width,
      inputCanvasElement.height,
    ) // Clear the canvas

    let scale
    if (img.width > img.height) {
      scale = inputCanvasElement.width / img.width
      console.log(`scale to width: ${inputCanvasElement.width}`)
    } else {
      scale = inputCanvasElement.height / img.height
      console.log(`scale to height: ${inputCanvasElement.height}`)
    }

    inputCanvas.drawImage(img, 0, 0, img.width * scale, img.height * scale)
    setOverlayImageObject({
      image: img,
      x: 0,
      y: 0,
      width: img.width * scale,
      height: img.height * scale,
      angle: 0,
    })
  }

  //Promise wrapper for Fabric's image object creation, which can handle a b64 data string or a URL for ajax.
  const getImg = (url, opts = {}) => {
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.onload = () => resolve(img)
      img.onerror = reject
      img.src = url
      Object.assign(img, opts)
    })
  }

  const activeImageChange = async (imgurl, inputCanvasElement) => {
    let img = await getImg(imgurl, { crossOrigin: 'anonymous' })

    const scale = Math.min(
      inputCanvasElement.width / img.width,
      inputCanvasElement.height / img.height,
    )

    const center = {
      x: (inputCanvasElement.width - img.width * scale) / 2,
      y: (inputCanvasElement.height - img.height * scale) / 2,
    }

    setActiveImageObject({
      image: img,
      x: center.x,
      y: center.y,
      width: img.width * scale,
      height: img.height * scale,
      angle: 0,
    })
  }

  const drawImage = (imageObject, canvas) => {
    const { image, x, y, width, height, angle } = imageObject

    canvas.save()
    canvas.translate(x + width / 2, y + height / 2)
    canvas.rotate((angle * Math.PI) / 180)
    canvas.drawImage(image, -width / 2, -height / 2, width, height)
    canvas.restore()
  }

  useEffect(() => {
    const canvasElement = canvasRef.current
    const canvas = canvasElement.getContext('2d')
    canvas.clearRect(0, 0, canvasElement.width, canvasElement.height)
    if (activeImageObject) {
      drawImage(activeImageObject, canvas)
    }
    if (overlayImageObject) {
      drawImage(overlayImageObject, canvas)
    }
  }, [overlayImageObject, activeImageObject, position])

  useEffect(() => {
    window.addEventListener('mouseup', handleMouseUp)
    return () => {
      window.removeEventListener('mouseup', handleMouseUp)
    }
  })

  useEffect(() => {
    if (imageCanvas !== null) {
      overlayImageChange(overlayImg, canvasRef.current)
    }
  }, [overlayImg])

  useEffect(() => {
    if (imageCanvas !== null) {
      activeImageChange(activeImg, canvasRef.current)
    }
  }, [activeImg])

  useEffect(() => {
    initCanvas(overlayImg, activeImg)
  }, [overlayImg, activeImg, onChangePhoto, canvasRef.current])

  return {
    imageCanvas,
    canvasRef,
    activeImageObject,
    overlayImageObject,
    zoomPhotoIn,
    zoomPhotoOut,
    rotateRight,
    rotateLeft,
    changePhoto,
    handleMouseDown,
    handleMouseUp,
    handleMouseMove,
  }
}
